/**
 * Service Worker Registration Utility
 * Registers and manages the service worker for offline capability
 */

import { Capacitor } from '@capacitor/core';

export const SW_UPDATE_EVENT = 'swUpdateAvailable';

// Track if a reload was already attempted in this session to prevent loops
const SESSION_RELOAD_KEY = 'sw_reload_attempted';

/**
 * Safe check for sessionStorage availability.
 * In some Incognito modes, the object exists but throws errors when used.
 */
const isSessionStorageAvailable = () => {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    const testKey = '__sw_test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Check if Service Worker is supported and safe to use.
 * We perform this check dynamically to avoid issues with WebView environments 
 * where navigator.serviceWorker might be flaky or change state.
 */
const getServiceWorker = () => {
  if (typeof navigator === 'undefined') return null;
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker || null;
};

/**
 * Register the service worker
 * @returns {Promise<ServiceWorkerRegistration|null>} Service worker registration or null
 */
export const registerServiceWorker = async () => {
  const sw = getServiceWorker();
  
  // Service Workers are not used/supported on native platforms (iOS/Android/Mac) 
  // in the same way as Web/PWA. Attempting to access navigator.serviceWorker 
  // is known to cause crashes in Capacitor/WKWebView.
  if (Capacitor.isNativePlatform() || !sw) {
    console.log('[Service Worker] Not supported or on native platform, skipping registration.');
    return null;
  }

  // Only register in production build
  if (process.env.NODE_ENV !== 'production') {
    await unregisterServiceWorker();
    return null;
  }

  try {
    // 1. Handle controller change
    let refreshing = false;
    sw.addEventListener('controllerchange', () => {
      if (refreshing) return;
      
      /**
       * CRITICAL: Only reload if we are sure it's an update.
       * We check if there's a pending reload flag in sessionStorage or window.name fallback.
       */
      const isStorageOk = isSessionStorageAvailable();
      let shouldReload = false;
      
      if (isStorageOk) {
        shouldReload = sessionStorage.getItem(SESSION_RELOAD_KEY) === 'true';
      } else {
        // Fallback for Incognito: window.name persists across reloads in the same tab
        shouldReload = window.name.includes(SESSION_RELOAD_KEY);
      }
      
      if (shouldReload) {
        refreshing = true;
        if (isStorageOk) {
          sessionStorage.removeItem(SESSION_RELOAD_KEY);
        } else {
          window.name = window.name.replace(SESSION_RELOAD_KEY, '').replace(/;;/g, ';');
        }
        console.log('[Service Worker] Update activated, reloading...');
        window.location.reload();
      }
    });

    // 2. Register
    const registration = await sw.register('/service-worker.js', {
      scope: '/'
    });

    // 3. Update check
    if (typeof registration.update === 'function') {
      registration.update().catch(err => console.warn('[Service Worker] Initial update check failed:', err));
      setInterval(() => {
        registration.update().catch(err => console.warn('[Service Worker] Periodic update check failed:', err));
      }, 120000); // Check every 2 mins
    }

    return registration;
  } catch (error) {
    console.error('[Service Worker] Registration failed:', error);
    return null;
  }
};

/**
 * Trigger update manually
 */
export const triggerUpdate = async () => {
  const sw = getServiceWorker();
  if (!sw) {
    window.location.reload();
    return;
  }

  try {
    const registration = await sw.getRegistration();
    if (registration && registration.waiting) {
      // Set the reload flag so the controllerchange listener knows to reload
      if (isSessionStorageAvailable()) {
        sessionStorage.setItem(SESSION_RELOAD_KEY, 'true');
      } else {
        // Fallback for Incognito: window.name survives reloads
        if (!window.name.includes(SESSION_RELOAD_KEY)) {
          window.name = `${window.name};${SESSION_RELOAD_KEY}`;
        }
      }
      
      // Tell worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Safari Fallback: If controllerchange doesn't fire in 2 seconds, force reload
      setTimeout(() => {
        const isStorageOk = isSessionStorageAvailable();
        const stillPending = isStorageOk 
          ? sessionStorage.getItem(SESSION_RELOAD_KEY) === 'true'
          : window.name.includes(SESSION_RELOAD_KEY);

        if (stillPending) {
          console.log('[Service Worker] Reload fallback triggered');
          if (isStorageOk) {
            sessionStorage.removeItem(SESSION_RELOAD_KEY);
          } else {
            window.name = window.name.replace(SESSION_RELOAD_KEY, '').replace(/;;/g, ';');
          }
          window.location.reload();
        }
      }, 2000);
    } else {
      // If no waiting worker, just refresh to clear any stale state
      window.location.reload();
    }
  } catch (err) {
    window.location.reload();
  }
};

/**
 * Unregister
 */
export const unregisterServiceWorker = async () => {
  const sw = getServiceWorker();
  if (!sw) return;

  try {
    const registration = await sw.getRegistration();
    if (registration) await registration.unregister();
  } catch (err) {
    console.warn('[Service Worker] Unregistration failed:', err);
  }
};

export default {
  register: registerServiceWorker,
  unregister: unregisterServiceWorker,
  triggerUpdate,
  SW_UPDATE_EVENT
};

